import type {
  StoreCreateCustomerAddress,
  StoreCustomer,
  StoreCustomerAddress,
} from "@medusajs/types";
import { type AnyFieldApi, useForm } from "@tanstack/react-form";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type InputHTMLAttributes, useState } from "react";
import { z } from "zod";
import { AddressSelects } from "#/components/checkout/AddressSelects";
import { PillBtn } from "#/components/primitives";
import {
  composeAddress2,
  splitBarangayFromAddress2,
} from "#/lib/checkout/cart-address";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import { Field } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import { medusa } from "#/lib/medusa";
import { notify } from "#/lib/notify";

const FIELD_CLASS = "h-12";

const addressesKeys = {
  all: ["storefront", "addresses"] as const,
  list: ["storefront", "addresses", "list"] as const,
};

const addressFormSchema = z.object({
  address_name: z.string().trim(),
  first_name: z.string().trim().min(1, "Required"),
  last_name: z.string().trim().min(1, "Required"),
  phone: z.string().trim(),
  company: z.string().trim(),
  address_1: z.string().trim().min(1, "Required"),
  address_2: z.string().trim(),
  barangay: z.string().trim(),
  city: z.string().trim().min(1, "Enter a city"),
  province: z.string().trim().min(1, "Enter a state / region"),
  postal_code: z.string().trim().min(3, "Required"),
  country_code: z
    .string()
    .trim()
    .length(2, "Use ISO-2 country code (e.g. ph)")
    .transform((v) => v.toLowerCase()),
});

type AddressFormValues = z.infer<typeof addressFormSchema>;

const EMPTY_FORM: AddressFormValues = {
  address_name: "Home",
  first_name: "",
  last_name: "",
  phone: "",
  company: "",
  address_1: "",
  address_2: "",
  barangay: "",
  city: "",
  province: "",
  postal_code: "",
  country_code: "ph",
};

function formFromAddress(addr: StoreCustomerAddress): AddressFormValues {
  const country_code = (addr.country_code ?? "ph").toLowerCase();
  const { barangay, address_2 } =
    country_code === "ph"
      ? splitBarangayFromAddress2(addr.address_2)
      : { barangay: "", address_2: addr.address_2 ?? "" };
  return {
    address_name: addr.address_name ?? "",
    first_name: addr.first_name ?? "",
    last_name: addr.last_name ?? "",
    phone: addr.phone ?? "",
    company: addr.company ?? "",
    address_1: addr.address_1 ?? "",
    address_2,
    barangay,
    city: addr.city ?? "",
    province: addr.province ?? "",
    postal_code: addr.postal_code ?? "",
    country_code,
  };
}

function toCreatePayload(
  form: AddressFormValues,
): StoreCreateCustomerAddress {
  const nullable = (v: string): string | null => (v.length > 0 ? v : null);
  const address2Combined = composeAddress2(form);
  return {
    address_name: nullable(form.address_name),
    first_name: nullable(form.first_name),
    last_name: nullable(form.last_name),
    phone: nullable(form.phone),
    company: nullable(form.company),
    address_1: nullable(form.address_1),
    address_2: nullable(address2Combined),
    city: nullable(form.city),
    province: nullable(form.province),
    postal_code: nullable(form.postal_code),
    country_code: form.country_code.toLowerCase(),
  };
}

type TextFieldProps = {
  field: AnyFieldApi;
  label: string;
  type?: InputHTMLAttributes<HTMLInputElement>["type"];
  autoComplete?: string;
  placeholder?: string;
  maxLength?: number;
};

function TextField({
  field,
  label,
  type = "text",
  autoComplete,
  placeholder,
  maxLength,
}: TextFieldProps) {
  const showError =
    field.state.meta.isTouched && field.state.meta.errors.length > 0;
  return (
    <Field label={label}>
      {(id) => (
        <>
          <Input
            id={id}
            name={field.name}
            type={type}
            value={String(field.state.value ?? "")}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            autoComplete={autoComplete}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-invalid={showError}
            aria-describedby={showError ? `${field.name}-error` : undefined}
            className={FIELD_CLASS}
          />
          {showError && (
            <p
              id={`${field.name}-error`}
              role="alert"
              className="mt-1 font-display text-destructive text-xs"
            >
              {field.state.meta.errors
                .map((e) =>
                  typeof e === "string" ? e : (e?.message ?? "Invalid"),
                )
                .join(", ")}
            </p>
          )}
        </>
      )}
    </Field>
  );
}

const customerWithAddressesQuery = queryOptions({
  queryKey: addressesKeys.list,
  queryFn: async (): Promise<StoreCustomer> => {
    const { customer } = await medusa.store.customer.retrieve({
      fields: "+addresses.*",
    });
    return customer;
  },
  staleTime: 30_000,
});

export const Route = createFileRoute("/account/addresses")({
  head: () => ({ meta: [{ title: "Addresses" }] }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(customerWithAddressesQuery),
  component: AddressesTab,
});

type EditingState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; addressId: string };

function AddressesTab() {
  const queryClient = useQueryClient();
  const { data: customer, isPending } = useQuery(customerWithAddressesQuery);
  const [editing, setEditing] = useState<EditingState>({ kind: "closed" });
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const addresses = customer?.addresses ?? [];

  const createMutation = useMutation({
    mutationFn: (payload: StoreCreateCustomerAddress) =>
      medusa.store.customer.createAddress(payload),
    onSuccess: ({ customer: updated }) => {
      queryClient.setQueryData(addressesKeys.list, updated);
      notify.success("Address added");
      setEditing({ kind: "closed" });
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : "Could not save address",
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      addressId,
      payload,
    }: {
      addressId: string;
      payload: StoreCreateCustomerAddress;
    }) => medusa.store.customer.updateAddress(addressId, payload),
    onSuccess: ({ customer: updated }) => {
      queryClient.setQueryData(addressesKeys.list, updated);
      notify.success("Address updated");
      setEditing({ kind: "closed" });
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : "Could not update address",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (addressId: string) =>
      medusa.store.customer.deleteAddress(addressId),
    onSuccess: ({ parent }) => {
      if (parent) queryClient.setQueryData(addressesKeys.list, parent);
      else queryClient.invalidateQueries({ queryKey: addressesKeys.list });
      notify.success("Address removed");
      setPendingDeleteId(null);
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : "Could not remove address",
      );
    },
  });

  const defaultMutation = useMutation({
    mutationFn: (addressId: string) =>
      medusa.store.customer.updateAddress(addressId, {
        is_default_shipping: true,
      }),
    onSuccess: ({ customer: updated }) => {
      queryClient.setQueryData(addressesKeys.list, updated);
      notify.success("Default shipping address updated");
    },
    onError: (err) => {
      notify.error(
        err instanceof Error ? err.message : "Could not update default address",
      );
    },
  });

  const editingAddress =
    editing.kind === "edit"
      ? (addresses.find((a) => a.id === editing.addressId) ?? null)
      : null;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h3 className="m-0 font-bold font-display text-2xl text-lp-text">
          Saved Addresses
        </h3>
        <PillBtn
          size="sm"
          variant="green"
          onClick={() => setEditing({ kind: "create" })}
        >
          Add address
        </PillBtn>
      </div>

      {isPending ? (
        <div
          role="status"
          aria-label="Loading addresses"
          className="py-12 text-center font-display text-lp-text-lt"
        >
          Loading…
        </div>
      ) : addresses.length === 0 ? (
        <div className="py-16 text-center font-display text-lp-text-lt">
          No saved addresses yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {addresses.map((a) => {
            const isDefault =
              customer?.default_shipping_address_id === a.id ||
              a.is_default_shipping;
            return (
              <div
                key={a.id}
                className="flex flex-col gap-3 rounded-2xl border-2 border-lp-green p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="font-bold font-display text-lp-green-dk">
                    {a.address_name ?? "Address"}
                  </div>
                  {isDefault && (
                    <span className="rounded-full bg-lp-green-soft px-3 py-1 font-display font-semibold text-lp-green-dk text-xs uppercase tracking-wide">
                      Default shipping
                    </span>
                  )}
                </div>
                <address className="m-0 font-display text-lp-text text-sm not-italic leading-relaxed">
                  {[a.first_name, a.last_name].filter(Boolean).join(" ")}
                  <br />
                  {a.address_1}
                  {a.address_2 ? (
                    <>
                      <br />
                      {a.address_2}
                    </>
                  ) : null}
                  <br />
                  {[a.city, a.province, a.postal_code]
                    .filter(Boolean)
                    .join(", ")}
                  <br />
                  {a.country_code?.toUpperCase()}
                  {a.phone ? (
                    <>
                      <br />
                      {a.phone}
                    </>
                  ) : null}
                </address>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setEditing({ kind: "edit", addressId: a.id })
                    }
                    className="cursor-pointer rounded-full border-2 border-lp-green-mid bg-white px-4 py-1.5 font-display font-semibold text-lp-green-mid text-sm hover:bg-lp-green-soft"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDeleteId(a.id)}
                    disabled={
                      deleteMutation.isPending &&
                      deleteMutation.variables === a.id
                    }
                    className="cursor-pointer rounded-full border-2 border-destructive bg-white px-4 py-1.5 font-display font-semibold text-destructive text-sm hover:bg-destructive/10 disabled:cursor-default disabled:opacity-60"
                  >
                    Delete
                  </button>
                  {!isDefault && (
                    <button
                      type="button"
                      onClick={() => defaultMutation.mutate(a.id)}
                      disabled={defaultMutation.isPending}
                      className="cursor-pointer rounded-full bg-transparent px-4 py-1.5 font-display font-semibold text-lp-green-dk text-sm hover:bg-lp-green-soft disabled:cursor-default disabled:opacity-60"
                    >
                      Set as default
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing.kind !== "closed" && (
        <AddressFormDialog
          key={editing.kind === "edit" ? editing.addressId : "create"}
          title={editing.kind === "edit" ? "Edit address" : "Add address"}
          initial={
            editingAddress ? formFromAddress(editingAddress) : { ...EMPTY_FORM }
          }
          isSaving={createMutation.isPending || updateMutation.isPending}
          onCancel={() => setEditing({ kind: "closed" })}
          onSubmit={(values) => {
            const payload = toCreatePayload(values);
            if (editing.kind === "edit") {
              updateMutation.mutate({ addressId: editing.addressId, payload });
            } else {
              createMutation.mutate(payload);
            }
          }}
        />
      )}

      <ConfirmDeleteDialog
        open={pendingDeleteId !== null}
        isDeleting={deleteMutation.isPending}
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId) deleteMutation.mutate(pendingDeleteId);
        }}
      />
    </div>
  );
}

interface AddressFormDialogProps {
  title: string;
  initial: AddressFormValues;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (values: AddressFormValues) => void;
}

function AddressFormDialog({
  title,
  initial,
  isSaving,
  onCancel,
  onSubmit,
}: AddressFormDialogProps) {
  const form = useForm({
    defaultValues: initial,
    validators: { 
      onMount: addressFormSchema,
      onChange: addressFormSchema 
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
      size="lg"
    >
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <form.Field name="address_name">
            {(field) => (
              <TextField
                field={field}
                label="Address name"
                placeholder="Home, Office, …"
              />
            )}
          </form.Field>
          <form.Field name="phone">
            {(field) => (
              <TextField
                field={field}
                label="Phone"
                type="tel"
                autoComplete="tel"
              />
            )}
          </form.Field>
          <form.Field name="first_name">
            {(field) => (
              <TextField
                field={field}
                label="First name"
                autoComplete="given-name"
              />
            )}
          </form.Field>
          <form.Field name="last_name">
            {(field) => (
              <TextField
                field={field}
                label="Last name"
                autoComplete="family-name"
              />
            )}
          </form.Field>
          <div className="md:col-span-2">
            <form.Field name="address_1">
              {(field) => (
                <TextField
                  field={field}
                  label="Address line 1"
                  autoComplete="address-line1"
                />
              )}
            </form.Field>
          </div>
          <div className="md:col-span-2">
            <form.Field name="address_2">
              {(field) => (
                <TextField
                  field={field}
                  label="Address line 2 (optional)"
                  autoComplete="address-line2"
                />
              )}
            </form.Field>
          </div>
          <div className="md:col-span-2">
            <form.Subscribe
              selector={(state) => ({
                country_code: state.values.country_code,
                province: state.values.province,
                city: state.values.city,
                barangay: state.values.barangay,
              })}
            >
              {(addr) => (
                <div className="flex flex-col gap-4">
                  <AddressSelects
                    value={addr}
                    onChange={(next) => {
                      form.setFieldValue("country_code", next.country_code);
                      form.setFieldValue("province", next.province);
                      form.setFieldValue("city", next.city);
                      form.setFieldValue("barangay", next.barangay);
                    }}
                  />
                </div>
              )}
            </form.Subscribe>
          </div>
          <form.Field name="postal_code">
            {(field) => (
              <TextField
                field={field}
                label="Postal code"
                autoComplete="postal-code"
              />
            )}
          </form.Field>
          <DialogFooter className="md:col-span-2 px-0 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="cursor-pointer rounded-full bg-transparent px-5 py-2 font-display text-lp-text hover:bg-lp-grey"
            >
              Cancel
            </button>
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
              })}
            >
              {({ canSubmit, isSubmitting }) => (
                <PillBtn
                  type="submit"
                  variant="green"
                  size="sm"
                  disabled={!canSubmit || isSubmitting || isSaving}
                >
                  {isSubmitting || isSaving ? "Saving…" : "Save address"}
                </PillBtn>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ConfirmDeleteDialogProps {
  open: boolean;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDeleteDialog({
  open,
  isDeleting,
  onCancel,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
      size="sm"
    >
      <DialogHeader>
        <DialogTitle>Delete this address?</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <p className="m-0 font-display text-lp-text">
          This address will be removed from your account. You can add it again
          later.
        </p>
      </DialogContent>
      <DialogFooter>
        <button
          type="button"
          onClick={onCancel}
          className="cursor-pointer rounded-full bg-transparent px-5 py-2 font-display text-lp-text hover:bg-lp-grey"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isDeleting}
          className="cursor-pointer rounded-full bg-destructive px-5 py-2 font-display font-semibold text-white hover:bg-destructive/90 disabled:cursor-default disabled:opacity-60"
        >
          {isDeleting ? "Removing…" : "Delete"}
        </button>
      </DialogFooter>
    </Dialog>
  );
}
